import type { SessionUser } from '@accounts/shared';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { STR } from './i18n';
import { useBranding } from './lib/branding';
import { useSession } from './lib/session';
import {
  CHANGE_PASSWORD_PATH,
  LOGIN_PATH,
  type Surface,
  type SurfaceId,
  SURFACES,
  canEnter,
  landingPath,
} from './lib/surfaces';
import { applyTheme, useAppearance } from './lib/theme';
import { LauncherPage } from './routes/LauncherPage';
import { LoginPage } from './routes/LoginPage';
import { ChangePasswordPage } from './routes/ChangePasswordPage';
import { SecurityPage } from './routes/SecurityPage';
import { TwoFactorChallengePage } from './routes/TwoFactorChallengePage';
import { TwoFactorSetupPage } from './routes/TwoFactorSetupPage';
import { NotFoundPage, RefusalPage } from './routes/NoticePage';
import { AccessHub } from './routes/admin/access/AccessHub';
import { AnnouncementsPage } from './routes/admin/AnnouncementsPage';
import { ApplicationsPage } from './routes/admin/ApplicationsPage';
import { AuditPage } from './routes/admin/AuditPage';
import { GrantsPage } from './routes/admin/GrantsPage';
import { SessionsPage } from './routes/admin/SessionsPage';
import { SettingsPage } from './routes/admin/SettingsPage';
import { UsersPage } from './routes/admin/UsersPage';
import { Toaster } from './ui/Toaster';

/**
 * The session gate and the router, both driven by the ONE surface table.
 *
 * Session state is discovered only by asking the server — the cookie is httpOnly, so there
 * is nothing for JavaScript to inspect. Everything here is affordance: the server enforces
 * the same gates independently and 403s anything that gets past this file.
 */

/**
 * Typed `Record<SurfaceId, …>`: a surface declared in the table with no screen behind it
 * fails the typecheck instead of shipping as an address that renders nothing.
 */
function screensFor(user: SessionUser | null): Record<SurfaceId, ReactNode> {
  return {
    login: <LoginPage />,
    twoFactorChallenge: <TwoFactorChallengePage />,
    twoFactorSetup: <TwoFactorSetupPage />,
    changePassword: user === null ? null : <ChangePasswordPage user={user} />,
    home: user === null ? null : <LauncherPage user={user} />,
    security: user === null ? null : <SecurityPage user={user} />,
    users: <UsersPage />,
    applications: <ApplicationsPage />,
    grants: <GrantsPage />,
    access: user === null ? null : <AccessHub section="overview" user={user} />,
    accessQueue: user === null ? null : <AccessHub section="queue" user={user} />,
    accessCatalog: user === null ? null : <AccessHub section="catalog" user={user} />,
    accessMatrix: user === null ? null : <AccessHub section="matrix" user={user} />,
    accessItEmails: user === null ? null : <AccessHub section="itEmails" user={user} />,
    accessEmails: user === null ? null : <AccessHub section="emails" user={user} />,
    audit: <AuditPage />,
    sessions: <SessionsPage />,
    announcements: <AnnouncementsPage />,
    settings: <SettingsPage section="general" />,
    settingsOperations: <SettingsPage section="operations" />,
    settingsEmail: <SettingsPage section="email" />,
    settingsAccess: <SettingsPage section="access" />,
  };
}

export function App() {
  const session = useSession();
  const branding = useBranding();
  const { palette, mode } = useAppearance();

  // R1 — the only place the two theme handles are written to <html>.
  useEffect(() => {
    applyTheme(palette, mode);
  }, [palette, mode]);

  // R2 — even the browser tab name comes from configuration, never from a literal.
  useEffect(() => {
    const name = branding.data?.name;
    if (name !== undefined && name !== '') document.title = name;
  }, [branding.data?.name]);

  if (session.isLoading) {
    return (
      <main
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--bg)',
          color: 'var(--text)',
        }}
      >
        <p className="tx-subtle">{STR.common.loading}</p>
      </main>
    );
  }

  const user = session.data ?? null;
  const screens = screensFor(user);

  return (
    <>
      <Routes>
        {SURFACES.map((surface) => (
          <Route
            key={surface.id}
            path={surface.path}
            element={<Guard surface={surface} user={user}>{screens[surface.id]}</Guard>}
          />
        ))}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Toaster />
    </>
  );
}

/**
 * THE ROUTE GUARD — the same `canEnter` test the rail uses to decide what to show, so the
 * two can never disagree.
 *
 * A refused actor never reaches the screen: the children are not rendered at all, so no
 * admin query is ever created for a non-admin and there is no interactive shell to fill with
 * empty tables. That failure mode — a page that renders and then 403s every read — is
 * exactly what this closes.
 */
function Guard({
  surface,
  user,
  children,
}: {
  surface: Surface;
  user: SessionUser | null;
  children: ReactNode;
}) {
  const location = useLocation();

  /*
   * THE WALL. A session holding a temporary password can reach exactly one screen; every
   * other authenticated endpoint 403s `password_change_required` until it is done.
   *
   * `location.search` rides along, exactly as the sign-in bounce below already does: dropping
   * it here would throw away a pending `?redirect=` and land somebody on the launcher after
   * their password change instead of where they were going. Carrying it is safe because the
   * client never turns it into a destination — it goes back to the SERVER with the password
   * change, which re-validates it from scratch against the live allow-list, and only the
   * server's answer is ever honoured.
   */
  if (user !== null && user.mustChangePassword && surface.id !== 'changePassword') {
    return <Navigate to={`${CHANGE_PASSWORD_PATH}${location.search}`} replace />;
  }
  // Not walled, so the wall's screen has nothing to offer.
  if (user !== null && !user.mustChangePassword && surface.id === 'changePassword') {
    return <Navigate to={landingPath()} replace />;
  }
  // Already signed in: the sign-in screen has nothing to offer.
  if (user !== null && surface.id === 'login') {
    return <Navigate to={landingPath()} replace />;
  }

  if (!canEnter(user, surface.capability)) {
    if (user === null) {
      // Preserve ?redirect= (and any other query string) across the transition to sign-in.
      return <Navigate to={`${LOGIN_PATH}${location.search}`} replace />;
    }
    return <RefusalPage />;
  }

  return <>{children}</>;
}
